import { useMemo, useState } from 'react'
import { Gift } from 'lucide-react'
import { format, parseISO } from 'date-fns'

import { PageShell } from '@/components/common/PageShell'
import { EmptyState } from '@/components/common/EmptyState'
import { CompanyChips } from '@/features/shifts/components/CompanyChips'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import {
  DataTable,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableCard,
} from '@/components/common/DataTable'
import { Badge, Select } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption, BadgeVariant } from '@/components/ui'
import { useMyCompanyFilter } from '@/features/companies/hooks/useMyCompanyFilter'
import { formatCurrency } from '@/lib/formatters'
import { useMyBonuses } from '../hooks/useMyBonuses'
import type { BonusEntryStatus } from '../types/bonus.types'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'reversed', label: 'Reversed' },
]

const STATUS_VARIANT: Record<BonusEntryStatus, BadgeVariant> = {
  pending_approval: 'warning',
  approved: 'success',
  reversed: 'neutral',
}

const STATUS_LABEL: Record<BonusEntryStatus, string> = {
  pending_approval: 'Pending approval',
  approved: 'Approved',
  reversed: 'Reversed',
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const LIMIT = 10

export function MyBonusesPage(): React.ReactElement {
  const [status, setStatus] = useState<string>('')
  const [page, setPage] = useState(1)
  const companyFilter = useMyCompanyFilter({ onChange: () => setPage(1) })
  const { companies: myCompanies, selectedCompanyIds, companyIds, companyNameById } = companyFilter

  const params = useMemo(
    () => ({
      ...(companyIds ? { companyIds } : {}),
      ...(status ? { status: status as BonusEntryStatus } : {}),
      page,
      limit: LIMIT,
    }),
    [companyIds, status, page]
  )

  const { data, isLoading, isError, error, refetch } = useMyBonuses(params)
  const records = data?.data ?? []
  const total = data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  const onChangeFilter = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v)
    setPage(1)
  }

  return (
    <PageShell
      breadcrumbItems={[{ label: 'My Workspace', to: '/dashboard' }, { label: 'My Bonuses' }]}
      icon={Gift}
      title="My Bonuses"
      description="Bonus entries awarded across every company you belong to."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <CompanyChips
              companies={myCompanies}
              selectedCompanyIds={selectedCompanyIds}
              onToggle={companyFilter.toggle}
            />
          }
        >
          <div className="w-full sm:w-45">
            <Select
              options={STATUS_OPTIONS}
              value={status}
              onChange={onChangeFilter(setStatus)}
            />
          </div>
        </WorkspaceToolbar>

        {isLoading ? (
          <DataTableCard>
            <SectionLoader variant="table" cols={5} rows={6} label="Loading bonuses" />
          </DataTableCard>
        ) : isError ? (
          <DataTableCard>
            <QueryErrorState
              error={error}
              title="Couldn't load your bonuses"
              onRetry={refetch}
              className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
            />
          </DataTableCard>
        ) : records.length === 0 ? (
          <EmptyState
            icon={Gift}
            title="No bonuses yet"
            description="When a bonus is issued to you, it'll show up here."
          />
        ) : (
          <DataTable>
            <DataTableHead>
              <tr>
                <DataTableHeadCell>Period</DataTableHeadCell>
                <DataTableHeadCell>Company</DataTableHeadCell>
                <DataTableHeadCell align="right">Amount</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
                <DataTableHeadCell>Approved</DataTableHeadCell>
              </tr>
            </DataTableHead>
            <DataTableBody>
              {records.map((b) => {
                const cName = companyNameById.get(b.companyId) ?? '—'
                const monthName = MONTHS[Math.max(0, Math.min(11, (b.month ?? 1) - 1))]
                return (
                  <DataTableRow key={b._id}>
                    <DataTableCell>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {monthName} {b.year}
                      </span>
                    </DataTableCell>
                    <DataTableCell>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{cName}</span>
                    </DataTableCell>
                    <DataTableCell align="right">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {formatCurrency(b.amount, b.currency)}
                      </span>
                    </DataTableCell>
                    <DataTableCell>
                      <Badge variant={STATUS_VARIANT[b.status]}>{STATUS_LABEL[b.status]}</Badge>
                    </DataTableCell>
                    <DataTableCell>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {b.approvedAt ? format(parseISO(b.approvedAt), 'd MMM yyyy') : '—'}
                      </span>
                    </DataTableCell>
                  </DataTableRow>
                )
              })}
            </DataTableBody>
          </DataTable>
        )}

        <TablePaginationBar
          page={page}
          limit={LIMIT}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </PageShell>
  )
}

MyBonusesPage.displayName = 'MyBonusesPage'
